<template>
  <v-container fluid>
    <v-layout justify-space-between align-center>
      <div class="d-flex align-center flex-wrap">
        <h2 class="mr-4">{{ $tt('customFieldsList') }}</h2>
        <!-- 圃場の新規登録はこの一覧から: マップで作成 / ファイルからインポート -->
        <v-btn small color="primary" class="mr-2" :to="{ name: 'FieldCreate' }">
          <v-icon small left>mdi-plus</v-icon>
          {{ $tt('customCreateNew') }}
        </v-btn>
        <v-btn small color="primary" outlined :to="{ name: 'FieldImport' }">
          <v-icon small left>mdi-upload</v-icon>
          {{ $tt('customImport') }}
        </v-btn>
      </div>
      <field-sort-order-component />
    </v-layout>

    <div v-if="fields.length > 0">
      <div v-resize="onResize">
        <div v-if="!isMobile">
          <!-- リスト表示 (行クリックでマップ上に表示)。「名称・面積」「登録年月日・有効期限」の2列 -->
          <v-row dense>
            <v-col cols="12" md="5" lg="4" class="list-pane">
              <v-simple-table dense class="list-scroll">
                <tbody>
                  <tr
                    v-for="f in fields"
                    :key="f.id"
                    :class="{ 'selected-field-row': String(selectedFieldPk) === String(f.pk) }"
                    style="cursor: pointer"
                    @click="selectField(f)"
                  >
                    <td width="36" class="pl-2 pr-0">
                      <user-icon-component
                        size="36"
                        :showNickname="true"
                        :iconUrl="getAccount(f).iconUrl"
                        :nickname="getAccount(f).nickname"
                      />
                    </td>
                    <td class="py-2">
                      <strong>{{ f.name }}</strong>
                      <div class="caption" v-html="$options.filters.aidAreaText(f.area, true)" />
                      <div v-for="p in listCurrentPlantings(f)" :key="p.pk">
                        <planting-indicator-component :planting="p" small :display-year="false" />
                      </div>
                    </td>
                    <td class="py-2">
                      <div class="caption grey--text">{{ fieldCreatedDate(f) }}</div>
                      <div v-if="fieldValidFrom(f)" class="caption grey--text">
                        {{ $tt('feed.fieldValidFromDate', { date: fieldValidFrom(f) }) }}
                      </div>
                      <div v-if="fieldValidTo(f)" class="caption grey--text">
                        {{ $tt('feed.fieldValidUntil', { date: fieldValidTo(f) }) }}
                      </div>
                    </td>
                    <td class="text-right pr-1" style="width: 40px">
                      <field-row-menu-component
                        :field="f"
                        :deletable="isMyField(f)"
                        @detail="showDetail(f)"
                        @remove="$refs.deleteDialog.openDeleteFieldDialog(f)"
                      />
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <!-- ページ選択・表示件数はリストの附属としてリスト直下に置く -->
              <pagination-component
                inline
                :pagination-info="paginationInfo"
                :paging-type="pagingType"
                @update-paging="updatePaging($event)"
              />
            </v-col>
            <v-col cols="12" md="7" lg="8">
              <fields-map-component
                :fields="fields"
                :highlighted-pks="selectedFieldPk ? [String(selectedFieldPk)] : []"
                height="75vh"
                @field-click="selectFieldByPk($event)"
              />
            </v-col>
          </v-row>
        </div>

        <!-- カード表示 -->
        <div v-else>
          <fields-map-component
            class="mb-3"
            :fields="fields"
            :highlighted-pks="selectedFieldPk ? [String(selectedFieldPk)] : []"
            height="40vh"
            @field-click="selectFieldByPk($event)"
          />
          <v-hover v-slot:default="{ hover }" v-for="f in fields" :key="f.id">
            <v-card :elevation="hover ? 12 : 2" color="mb-4">
              <v-list>
                <v-list-item two-line>
                  <v-list-item-avatar>
                    <user-icon-component
                      size="36"
                      :showNickname="true"
                      :iconUrl="getAccount(f).iconUrl"
                      :nickname="getAccount(f).nickname"
                    />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ f.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      <span class="font-weight-bold mr-1">{{ f.area | aidAreaText }}</span>
                      <span>( {{ f.length | aidDistanceText }} )</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <div v-for="p in listCurrentPlantings(f)" :key="p.pk">
                        <planting-indicator-component :planting="p" small :display-year="false" />
                      </div>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>
                      <div>{{ fieldCreatedDate(f) }}</div>
                      <div v-if="fieldValidFrom(f)">
                        {{ $tt('feed.fieldValidFromDate', { date: fieldValidFrom(f) }) }}
                      </div>
                      <div v-if="fieldValidTo(f)">{{ $tt('feed.fieldValidUntil', { date: fieldValidTo(f) }) }}</div>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <v-card-actions>
                <div class="flex-grow-1"></div>
                <field-row-menu-component
                  :field="f"
                  :deletable="isMyField(f)"
                  @detail="showDetail(f)"
                  @remove="$refs.deleteDialog.openDeleteFieldDialog(f)"
                />
              </v-card-actions>
            </v-card>
          </v-hover>
          <pagination-component
            inline
            :pagination-info="paginationInfo"
            :paging-type="pagingType"
            @update-paging="updatePaging($event)"
          />
        </div>
      </div>
    </div>
    <v-row v-else justify="center" align-content="center">
      <v-col cols="8" class="pt-12">
        <v-img class="img-fluid" src="/images/fields_list_no_fields.jpg" />
      </v-col>
    </v-row>

    <field-delete-dialog-component ref="deleteDialog" />
  </v-container>
</template>

<script>
import FieldDeleteDialogComponent from '@/components/fields/FieldDeleteDialogComponent'
import FieldRowMenuComponent from '@/components/fields/FieldRowMenuComponent'
import FieldSortOrderComponent from '@/components/fields/FieldSortOrderComponent'
import FieldsMapComponent from '@/components/fields/FieldsMapComponent'
import FieldListMixin from '@/components/mixins/field-list-mixin'
import PaginationComponent from '@/components/PaginationComponent'
import PlantingIndicatorComponent from '@/components/planting/PlantingIndicatorComponent'
import UserIconComponent from '@/components/user/UserIconComponent'
import feedApiClient from '@/lib/feed-api-client'

export default {
  name: 'FieldListAsList',
  mixins: [FieldListMixin],
  components: {
    PlantingIndicatorComponent,
    UserIconComponent,
    FieldDeleteDialogComponent,
    FieldRowMenuComponent,
    PaginationComponent,
    FieldSortOrderComponent,
    FieldsMapComponent,
  },
  data() {
    return {
      selectedFieldPk: null,
      fieldAttrs: {},
    }
  },
  mounted() {
    this.loadFieldAttrs()
  },
  methods: {
    selectField(field) {
      this.selectedFieldPk = String(this.selectedFieldPk) === String(field.pk) ? null : field.pk
    },
    selectFieldByPk(pk) {
      this.selectedFieldPk = String(this.selectedFieldPk) === String(pk) ? null : pk
    },
    // 圃場属性 (有効期間) は feed-api 管理。取れなくても一覧表示は継続する
    async loadFieldAttrs() {
      try {
        const res = await feedApiClient.fieldAttrs()
        this.fieldAttrs = res.data.attrs || {}
      } catch (e) {
        console.error(e.response || e)
      }
    },
    fieldValidFrom(field) {
      return (this.fieldAttrs[String(field.pk)] || {}).valid_from || null
    },
    fieldValidTo(field) {
      return (this.fieldAttrs[String(field.pk)] || {}).valid_to || null
    },
  },
}
</script>

<style scoped>
.footer-select-box {
  width: 90px;
}
.selected-field-row {
  background-color: rgba(102, 187, 106, 0.15);
}
/* リスト列: マップと同じ高さで、テーブルはスクロール・ページャーは最下段に固定 */
.list-pane {
  display: flex;
  flex-direction: column;
  height: 75vh;
}
.list-pane .list-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
}
</style>
